import React, { createContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode as decode } from 'jwt-decode';
import axios from 'axios';
import apiClient from '../services/api/apiClient.js';

export const AuthContext = createContext();

/**
 * `hasPermiso` replica EXACTAMENTE la misma lógica de evaluación de permisos que
 * `core/middleware/auth.middleware.js#tienePermisoNormalizado` en el backend — deben
 * mantenerse sincronizadas: "ocultar un botón" y "el backend lo rechaza" tienen que
 * ser siempre consistentes. Acepta `'recurso:accion'` (formato nuevo) y hace fallback
 * al flag plano legado de V1 si `user[permiso]` coincide literal.
 */
function evaluarPermiso(user, permiso) {
  if (!user) return false;
  if (user.role === 'admin' || user.permisos?.admin === true || user.menuAdmin === true) return true;
  if (user[permiso] === true) return true;
  const [recurso, accion] = permiso.split(':');
  const val = user.permisos?.[recurso];
  if (!val || typeof val !== 'object') return false;
  return val[accion] === true;
}

/**
 * Lee la sesión guardada de forma SÍNCRONA (sin esperar a un `useEffect`).
 *
 * BUG REAL CORREGIDO 2026-09-28 (encontrado probando en el navegador: cualquier
 * refresh/navegación directa a una ruta protegida rebotaba a `/login` aunque hubiera
 * un token válido en `localStorage`): antes, `isAuth`/`user` arrancaban en
 * `false`/`null` y solo se corregían dentro de un `useEffect` (que corre DESPUÉS del
 * primer render) -- pero `PrivateRoute.jsx` lee `isAuth` en ESE primer render y ya
 * redirige a `/login` antes de que el efecto alcance a correr. Se resuelve leyendo el
 * token de forma síncrona vía inicializador perezoso de `useState`.
 */
function leerSesionInicial() {
  const stored = localStorage.getItem('app_token');
  if (!stored) return { token: '', isAuth: false, user: null };
  try {
    const decoded = decode(stored);
    const ahora = Math.floor(Date.now() / 1000);
    if (ahora >= decoded.exp) {
      localStorage.removeItem('app_token');
      return { token: '', isAuth: false, user: null };
    }
    return { token: stored, isAuth: true, user: decoded };
  } catch {
    localStorage.removeItem('app_token');
    return { token: '', isAuth: false, user: null };
  }
}

const AuthContextProvider = ({ children }) => {
  const [sesionInicial] = useState(leerSesionInicial);
  const [token, setToken] = useState(sesionInicial.token);
  const [isAuth, setIsAuth] = useState(sesionInicial.isAuth);
  const [user, setUser] = useState(sesionInicial.user);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [shouldShowPaymentWarning, setShouldShowPaymentWarning] = useState(false);

  const isInPaymentPeriod = () => {
    const today = new Date();
    const currentDay = today.getDate();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    return currentDay >= 10 && currentDay <= lastDayOfMonth;
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(import.meta.env.VITE_URL_SALDO_SISTEMIFY);
      const { estado } = response.data;
      setPaymentStatus(estado);
      setShouldShowPaymentWarning(estado === 'Con Adeudo' && isInPaymentPeriod());
    } catch (error) {
      setPaymentStatus('');
      setShouldShowPaymentWarning(false);
    }
  };

  const loginUser = (nuevoToken, userPayload) => {
    localStorage.setItem('app_token', nuevoToken);
    setToken(nuevoToken);
    setIsAuth(true);
    setUser(userPayload);
    checkPaymentStatus();
  };

  const logoutUser = () => {
    localStorage.removeItem('app_token');
    setToken('');
    setIsAuth(false);
    setUser(null);
    setPaymentStatus('');
    setShouldShowPaymentWarning(false);
  };

  const refreshUser = async () => {
    const { data } = await apiClient.get('/auth/me');
    setUser(data.data);
    return data.data;
  };

  const hasPermiso = useCallback((permiso) => evaluarPermiso(user, permiso), [user]);
  const isAdmin = useCallback(() => user?.role === 'admin' || user?.permisos?.admin === true || user?.menuAdmin === true, [user]);

  // La sesión ya se restauró de forma síncrona (ver `leerSesionInicial` arriba) --
  // este efecto solo dispara el side-effect de red que sí puede esperar al montaje.
  useEffect(() => {
    if (isAuth) checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token, isAuth, user, paymentStatus, shouldShowPaymentWarning,
        loginUser, logoutUser, checkPaymentStatus, refreshUser, hasPermiso, isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
